import { Controller } from "@hotwired/stimulus"
import isMobile from "@client/utils/isMobile"

export default class extends Controller {
  static targets = ["regular", "search", "mobile"]
  static values = {
    visiblePages: Array
  }

  connect() {
    this.tweakwiseOutput = document.querySelector('#tweakwise-output')
    this.observers = []

    if (!this.tweakwiseOutput) {
      return
    }

    this.currentPage = this.getCurrentPage()
    this.shouldShowRegular = this.visiblePagesValue.includes(this.currentPage)

    // Mount React components inside this controller if not already mounted
    this.mountReactComponents()

    if (isMobile()) {
      this.setupMobile()
    } else {
      this.setupDesktop()
    }

    this.handleHashChange = this.handleHashChange.bind(this)
    this.handleCloseClick = this.handleCloseClick.bind(this)
    window.addEventListener('hashchange', this.handleHashChange)
    this.setupCloseButtonListener()
  }

  disconnect() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []

    window.removeEventListener('hashchange', this.handleHashChange)
  }

  setupDesktop() {
    if (!this.hasSearchTarget) {
      return
    }

    if (this.hasRegularTarget) {
      if (this.currentPage === 'landing_pages#home') {
        this.regularTarget.classList.add('d-none')
      } else if (this.currentPage === 'products#show' && !window.location.hash.includes('twn')) {
        this.regularTarget.classList.add('d-none')
      }
    }

    const observer = new MutationObserver(() => this.updateDesktopVisibility())
    observer.observe(this.tweakwiseOutput, {
      attributes: true,
      attributeFilter: ['style']
    })
    this.observers.push(observer)

    this.updateDesktopVisibility()
  }

  setupMobile() {
    if (!this.hasMobileTarget) {
      return
    }

    const overlayWatcher = new MutationObserver(() => {
      const overlay = document.querySelector('#twn-starter-overlay')

      if (overlay) {
        const overlayObserver = new MutationObserver(() => this.updateMobileVisibility())
        overlayObserver.observe(overlay, {
          attributes: true,
          attributeFilter: ['style']
        })
        this.observers.push(overlayObserver)

        this.updateMobileVisibility()
      }
    })

    overlayWatcher.observe(document.body, {
      childList: true,
      subtree: true
    })
    this.observers.push(overlayWatcher)
  }

  updateDesktopVisibility() {
    const isSearchActive = this.isSearchActive()

    if (this.hasSearchTarget) {
      this.searchTarget.classList.toggle('d-none', !isSearchActive)
    }

    if (this.hasRegularTarget) {
      if (isSearchActive) {
        this.regularTarget.classList.add('d-none')
      } else if (this.shouldShowRegular) {
        const isProductShow = this.currentPage === 'products#show'
        const hasSearchHash = window.location.hash.includes('twn')
        if (isProductShow && !hasSearchHash) {
          this.regularTarget.classList.add('d-none')
        } else {
          this.regularTarget.classList.remove('d-none')
        }
      }
    }
  }

  updateMobileVisibility() {
    const overlay = document.querySelector('#twn-starter-overlay')
    const isSearchActive = overlay && window.getComputedStyle(overlay).display === 'block'

    if (!this.hasMobileTarget) return

    if (isSearchActive) {
      const header = overlay.querySelector('.twn-starter__header')
      if (header && !overlay.contains(this.mobileTarget)) {
        header.insertAdjacentElement('afterend', this.mobileTarget)
      }
      this.mobileTarget.style.display = 'block'
    } else {
      this.mobileTarget.style.display = 'none'
    }
  }

  isSearchActive() {
    // On category pages and search pages, always show the regular cart picker
    if (this.currentPage === 'products#index' || this.currentPage === 'pages#search') {
      return false
    }

    return false
  }

  getCurrentPage() {
    const body = document.body
    const controller = body.dataset.controller || ''
    const action = body.dataset.action || ''
    return `${controller}#${action}`
  }

  mountReactComponents() {
    if (document.readyState === 'complete') {
      this.doMountReactComponents()
    } else {
      window.addEventListener('load', () => this.doMountReactComponents(), { once: true })
    }
  }

  doMountReactComponents() {
    const reactElements = this.element.querySelectorAll('[data-react-class]')
    const unmountedElements = Array.from(reactElements).filter(el => el.children.length === 0)

    if (unmountedElements.length > 0 && window.ReactRailsUJS) {
      // Remove cache IDs that might prevent remounting
      unmountedElements.forEach(el => {
        el.removeAttribute('data-react-cache-id')
      })

      // Mount components on the whole document to ensure all are caught
      window.ReactRailsUJS.mountComponents()
    }
  }

  setupCloseButtonListener() {
    document.body.addEventListener('click', (e) => {
      if (e.target.closest('.twn-starter__header-close')) {
        this.handleCloseClick()
      }
    }, { once: false })
  }

  handleCloseClick() {
    if (!this.hasRegularTarget && !this.hasSearchTarget) {
      return
    }
    const desktopInput = document.querySelector('#tweakwise-input-desktop')
    const mobileInput = document.querySelector('#tweakwise-input-mobile')
    if (desktopInput) {
      desktopInput.value = ''
      desktopInput.dispatchEvent(new Event('input', { bubbles: true }))
      desktopInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
    if (mobileInput) {
      mobileInput.value = ''
      mobileInput.dispatchEvent(new Event('input', { bubbles: true }))
      mobileInput.dispatchEvent(new Event('change', { bubbles: true }))
    }
    if (window.location.hash.includes('twn')) {
      history.replaceState(null, null, window.location.pathname)
      const isProductShow = this.getCurrentPage() === 'products#show'
      if (isProductShow) {
        if (this.hasRegularTarget) {
          this.regularTarget.classList.add('d-none')
        }
        if (this.hasSearchTarget) {
          this.searchTarget.classList.add('d-none')
        }
      }
    }
  }

  handleHashChange() {
    this.updateVisibility()
  }

  updateVisibility() {
    if (isMobile()) {
      this.updateMobileVisibility()
    } else {
      this.updateDesktopVisibility()
    }
  }
}
